import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';

import { useWorkspace } from '@/features/workspaces';
import { useCanWork } from '@/features/workspaces/canWork';
import { BoardLimitNotice, useLimitState } from '@/features/spend-limits';
import {
  CapacityMeter,
  WorkspaceConcurrencyProvider,
} from '@/features/concurrency';
import { PersonalAccountNotice, useIsOrgAdmin } from '@/features/identity';
import { MissingPartsNotice } from '@/features/library';
import { resolveLabelNarrowing, type LabelNarrowing } from '@/features/labels';
import { NotFoundState } from '@/shared/components/NotFoundState';
import { EyeIcon, NetworkIcon, PencilIcon, PlusIcon } from 'lucide-react';

import { Button, buttonVariants } from '@/components/ui/button';
import { usePhoneWidth } from '@/shared/hooks/usePhoneWidth';
import { BoardPhoneActions } from './BoardPhoneActions';
import { ExpandableText } from '@/shared/components/ExpandableText';
import { LiveStatus } from '@/shared/components/LiveStatus';
import { PageHeader } from '@/shared/components/PageHeader';
import { useReducedMotion } from '@/shared/hooks/useReducedMotion';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import { PendingRegion } from '@/shared/components/PendingRegion';
import { Skeleton } from '@/components/ui/skeleton';
import { LIVE_POLL_MS } from '../liveCadence';
import { useWorkspaceQueue } from '../useWorkspaceQueue';
import { BoardDiagramLazy } from './BoardDiagramLazy';

import { TaskFilterRow } from './TaskFilterRow';
import { narrowingSummary } from '../narrowingSummary';
import {
  deriveCardExecutionState,
  hasNonTerminalActivity,
} from '../executionStatus';
import { useIsTriggerRefreshActive } from '../triggerRefreshStore';
import type { TaskView, WorkflowView } from '@/lib/api/generated/model';
import { buildBoard, type Board } from '../boardModel';
import { type BoardStateFilter, type TaskTab } from '../searchParams';
import { filterBoard, type TaskNarrowing } from '../boardFilter';
import { locateTask, stepLabelsOf } from '../locateTask';
import { useTaskDraftStore } from '../drafts/taskDraftStore';
import { BoardCanvasLazy } from './BoardCanvasLazy';
import { useArrivalHold } from '../hooks/useArrivalHold';
import { useInteractionHold } from '../interactionHold';
import { ReadFailure } from '@/shared/components/ReadFailure';
import { BoardSelectionBar } from './BoardSelectionBar';
import { useBoardSelectionStore } from '../boardSelectionStore';
import {
  BoardMoveContext,
  BoardStepsContext,
  type BoardMove,
} from '../boardStepsContext';
import { BoardUnavailable } from './BoardUnavailable';
import { TaskDrawer } from './TaskDrawer';
import { useRememberWorkflow } from '@/shared/hooks/usePageChrome';

export interface BoardPageProps {
  workspaceId: string;
  /** Which workflow's board this is, from the `$workflowKey` path parameter. */
  workflowKey: string;
  /** The open task, from the address. The board renders what it is told. */
  openTaskId?: string;
  /** Absent when the address names no tab; the drawer then decides (spec 095). */
  openTab?: TaskTab;
  onOpenTask?: (taskId: string) => void;
  onCloseTask?: () => void;
  onTabChange?: (tab: TaskTab, options?: { replace?: boolean }) => void;
  /** The file shown beside the open task, from the address; the drawer's. */
  previewPath?: string;
  /** Takes `preview` out of the address; required, as the drawer's is. */
  onPreviewClose: () => void;
  /** Which state the board is narrowed to, from the address. */
  stateFilter?: BoardStateFilter;
  onStateFilterChange?: (state: BoardStateFilter | undefined) => void;
  /** Which labels the board is narrowed to, from the address. */
  labelNarrowing?: LabelNarrowing;
  onLabelNarrowingChange?: (next: LabelNarrowing) => void;
  /** The text the board is narrowed to, from the address. */
  query?: string;
  onQueryChange?: (next: string | undefined) => void;
  /** Whether the workflow's diagram is shown beside the board, from the address. */
  showDiagram?: boolean;
  onShowDiagramChange?: (next: boolean) => void;
  /**
   * The address asked for the New task dialog (`?create=true`), which is how
   * the command palette runs that action. While it is set the dialog is open;
   * dismissing the dialog reports back so the route can take the parameter out
   * of the address again.
   */
  createRequested?: boolean;
  onCreateDismissed?: () => void;
}

function filterTasksForWorkflow(
  tasks: TaskView[] | undefined,
  workflowId: string,
): TaskView[] {
  return (tasks ?? []).filter((task) => task.workflow_id === workflowId);
}

interface BoardActivity {
  running: number;
  waiting: number;
  failed: number;
}

/**
 * What the board is doing right now, counted from the same derivation the
 * cards use so the summary can never disagree with them. A task count is not
 * why anyone opens a board; whether anything is running, stuck waiting on a
 * person, or has failed is.
 */
function summariseActivity(board: Board): BoardActivity {
  const activity: BoardActivity = { running: 0, waiting: 0, failed: 0 };
  for (const column of board.columns) {
    for (const step of column.steps) {
      for (const task of step.tasks) {
        const state = deriveCardExecutionState(task, step);
        if (state.kind === 'live') activity.running += 1;
        else if (state.kind === 'waiting-human') activity.waiting += 1;
        else if (state.kind === 'failed') activity.failed += 1;
      }
    }
  }
  return activity;
}

const taskId = (task: TaskView) => task.id;

/** Every step a task can be drawn in, as `buildBoard` places them. */
function stepIdsOf(workflow: WorkflowView): ReadonlySet<string> {
  return new Set([
    ...workflow.phases.flatMap((phase) =>
      (phase.steps ?? []).map((step) => step.id),
    ),
    workflow.start_step.id,
    workflow.done_step.id,
  ]);
}

/** How many tasks a board is showing, counted from what is actually drawn. */
function countTasks(board: Board): number {
  let total = 0;
  for (const column of board.columns) {
    for (const step of column.steps) total += step.tasks.length;
  }
  return total;
}

/**
 * One workflow's board. The workflow is named by the `$workflowKey` path
 * parameter, so this page is about exactly one board — which workflow you are
 * looking at is chosen on the Boards index (`workflows/BoardsPage`), the
 * switcher that lists every workflow in the workspace.
 *
 * A key naming no workflow in this workspace is a not-found condition rather
 * than something to fall back from: a path parameter names a resource, and
 * silently showing a different workflow's board would be a lie about which
 * board the address points at.
 */
export function BoardPage({
  workspaceId,
  workflowKey,
  openTaskId,
  openTab,
  onOpenTask,
  onCloseTask,
  onTabChange,
  previewPath,
  onPreviewClose,
  stateFilter,
  onStateFilterChange,
  labelNarrowing,
  onLabelNarrowingChange,
  query,
  onQueryChange,
  showDiagram = false,
  onShowDiagramChange,
  createRequested = false,
  onCreateDismissed,
}: BoardPageProps) {
  // "+" and the address both open this board's task draft (spec 121), which
  // the workspace's dock shows — so it outlives this page. The address's
  // request is spent as it is honoured, so a reload does not ask again: the
  // draft itself is what survives.
  const openDraft = useTaskDraftStore((s) => s.openDraft);
  // Hidden tasks are off the board by design; this is the way back to them (078 FR-017).
  // Local rather than a URL param: it is a momentary "let me look", not a view worth
  // sharing or restoring, and putting it in the address bar would make an ordinary board
  // link occasionally show tasks the recipient did not expect.
  const [showHidden, setShowHidden] = useState(false);
  // Subtasks (`task.is_subtask`) are on the board by default, tinted rather
  // than hidden — this narrows them back out. Local state, not a URL param,
  // same reasoning as `showHidden`, and purely client-side: unlike
  // `showHidden` it never touches `useWorkspace`, since the backend already
  // always returns subtasks.
  const [showSubtasks, setShowSubtasks] = useState(true);
  // The sidebar keeps the workflows this device opens near the top (spec 128).
  useRememberWorkflow(workspaceId, workflowKey);

  // Initial fetch
  const { data: workspace, isPending } = useWorkspace(workspaceId, {
    includeHidden: showHidden,
  });

  // A placeholder only once the wait is worth remarking on (spec 079 FR-017).
  const showPlaceholder = usePendingDisplay(isPending);

  // A view transition is not a style rule, so the preference is asked here
  // rather than left to the stylesheet (spec 075 FR-031).
  const reducedMotion = useReducedMotion();
  const canWork = useCanWork();
  const isPhone = usePhoneWidth();
  const workflow = workspace?.workflows.find((w) => w.key === workflowKey);
  const allTasks = useMemo(
    () => filterTasksForWorkflow(workspace?.tasks, workflow?.id ?? ''),
    [workspace?.tasks, workflow?.id],
  );
  // New tasks arrive in place (spec 148), held back only while the person is
  // in the middle of something: a drag, an open card menu, or the pointer or
  // focus in the column the task would land in. The person's own arrive at
  // once. A different workflow or the hidden-task toggle is a new view, not an
  // arrival.
  const dragging = useInteractionHold((state) => state.dragging);
  const menuOpen = useInteractionHold((state) => state.menusOpen > 0);
  const pointerZone = useInteractionHold((state) => state.pointerZone);
  const focusZone = useInteractionHold((state) => state.focusZone);
  const stepIds = useMemo(
    () => (workflow ? stepIdsOf(workflow) : null),
    [workflow],
  );
  const isHolding = useCallback(
    (task: TaskView) => {
      if (dragging || menuOpen) return true;
      if (!workflow || !stepIds) return false;
      // Where `buildBoard` will draw it: its step, or Backlog for a position
      // that names none.
      const landing = stepIds.has(task.position)
        ? task.position
        : workflow.start_step.id;
      return landing === pointerZone || landing === focusZone;
    },
    [dragging, focusZone, menuOpen, pointerZone, stepIds, workflow],
  );
  const arrivals = useArrivalHold(
    allTasks,
    `${workflow?.id ?? ''}|${String(showHidden)}`,
    taskId,
    isHolding,
  );
  const tasks = arrivals.visible;

  // Enable polling when tasks are live (FR-012, R4), or briefly after a
  // local move in case a backend trigger is still writing follow-on changes.
  const triggerRefreshActive = useIsTriggerRefreshActive(workspaceId);
  const live =
    hasNonTerminalActivity(workspace?.tasks ?? []) || triggerRefreshActive;
  // Polling stays conditional on work in flight (constitution, Server State
  // Freshness); an idle page refreshes when the reader comes back to the tab
  // (spec 126 R1) and says how old its data is. Paused while a card is being
  // dragged, so nothing moves under the pointer.
  const refresh = useWorkspace(workspaceId, {
    refetchInterval: live && !dragging ? LIVE_POLL_MS : false,
    refetchOnWindowFocus: 'always',
    // Must match the read above, or polling would refresh a different cache entry and
    // the board would sit on a snapshot that never updates while hidden tasks are shown.
    includeHidden: showHidden,
  });

  // Only once the board can hold a task: a request that arrives on an
  // unpublished workflow waits in the address and does nothing.
  const canCreate = workflow !== undefined && workflow.status !== 'draft';
  useEffect(() => {
    if (!createRequested || !canCreate) return;
    openDraft(workspaceId, workflow.id);
    onCreateDismissed?.();
  }, [
    canCreate,
    createRequested,
    onCreateDismissed,
    openDraft,
    workflow,
    workspaceId,
  ]);

  const result = useMemo(
    () => (workflow ? buildBoard(workflow, tasks) : null),
    [workflow, tasks],
  );

  // Above every early return, like the hooks below it: the not-found and draft
  // states return before the notice is rendered, and a hook after them would
  // run on some renders and not others.
  const limits = useLimitState(workspaceId, workflow?.id);
  const isOrgAdmin = useIsOrgAdmin();
  // The whole workspace's line, for the capacity meter's popover (spec 118).
  const queue = useWorkspaceQueue(workspace);

  // Beside the other hooks and above every early return: the states below
  // return before the drawer exists, and a hook after them runs on some
  // renders and not others.
  const stepLabels = useMemo(
    () => (result?.ok === true ? stepLabelsOf(result.board) : new Map()),
    [result],
  );
  // Every step of the unfiltered board in board order — what the open task's
  // next action can start at (spec 125). Unfiltered for the same reason the
  // labels are: narrowing the board must not shrink the choices.
  const boardSteps = useMemo(
    () =>
      result?.ok === true
        ? result.board.columns.flatMap((column) => column.steps)
        : [],
    [result],
  );
  // The canvas's "Move to…" (spec 262), so the drawer's menu offers it too.
  // Kept as a value, hence the updater: a function handed to a state setter
  // is called, not stored.
  const [boardMove, setBoardMove] = useState<BoardMove | null>(null);
  const registerMove = useCallback((move: BoardMove | null) => {
    setBoardMove(() => move);
  }, []);

  // A selection is a moment's intent on one view of the board (spec 098
  // FR-014): a change of filter or a departure ends it.
  const clearSelection = useBoardSelectionStore((state) => state.clear);
  const narrowingKey = JSON.stringify(labelNarrowing ?? null);
  useEffect(() => {
    clearSelection();
  }, [clearSelection, query, stateFilter, narrowingKey]);
  useEffect(() => () => clearSelection(), [clearSelection]);

  if (isPending) {
    // Nothing at all until the wait is worth remarking on (spec 079 FR-017):
    // a board that paints two grey slabs and takes them away again on a warm
    // cache is the flicker the delay exists to prevent.
    if (!showPlaceholder) return null;
    return (
      <section className="flex flex-1 flex-col gap-6 max-md:px-4 max-md:pt-4 p-6">
        <PendingRegion label="Loading board" className="gap-4">
          <Skeleton className="h-6 w-48 rounded-md" />
          {/* Shaped like the board it stands in for (spec 126 FR-013). */}
          <div className="flex flex-1 gap-3" aria-hidden="true">
            {[0, 1, 2].map((column) => (
              <div key={column} className="flex w-72 flex-col gap-2">
                <Skeleton className="h-8 rounded-md" />
                <Skeleton className="h-24 rounded-lg" />
                <Skeleton className="h-24 rounded-lg" />
              </div>
            ))}
          </div>
        </PendingRegion>
      </section>
    );
  }

  // `useWorkspace` resolves to `null` on a failed read rather than throwing —
  // that must not be rendered as "no workflows".
  if (workspace === null) {
    return (
      <section className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
        <ReadFailure
          message="The board could not be loaded."
          onRetry={() => {
            void refresh.refetch();
          }}
          isFetching={refresh.isFetching}
        />
      </section>
    );
  }

  // The address names a workflow this workspace does not have. That is a
  // not-found condition, not an empty board — the Workflows list is where you
  // pick a workflow that does exist (the Boards section it used to point at was
  // folded into Workflows by spec 094; only its redirect remains).
  if (!workflow) {
    return (
      <NotFoundState
        title="Board not found"
        description="This workspace has no workflow with that key. It may have been renamed or removed since the link was shared."
        action={
          <Link
            to="/workspaces/$workspaceId/workflows"
            params={{ workspaceId }}
            className="text-foreground rounded-sm text-sm underline underline-offset-4"
          >
            Back to workflows
          </Link>
        }
      />
    );
  }

  // A draft has no committed definition, so it has no columns and cannot hold a
  // task (FR-004). This page finds its workflow directly in the aggregate rather
  // than through `boardEligibleWorkflows`, so the rule has to be stated again
  // here — the board surfaces that *do* use that helper simply never link to a
  // draft, but a hand-typed or bookmarked address still arrives.
  if (workflow.status === 'draft') {
    return (
      <NotFoundState
        title="This workflow is still a draft"
        description="A board appears once the workflow is published. Open it in the builder to finish and publish it."
        action={
          <Link
            to="/workspaces/$workspaceId/workflows/$workflowKey/builder"
            params={{ workspaceId, workflowKey }}
            className="text-foreground rounded-sm text-sm underline underline-offset-4"
          >
            Open in the builder
          </Link>
        }
      />
    );
  }

  // New tasks start at the workflow's own start step — never at a phase step
  // that happens to be of `backlog` type, which is an ordinary step.
  const startStepId = workflow.start_step.id;
  // The vocabulary is the workspace's, not this board's: a label defined while
  // looking at another workflow is offered here too. Read through the optional
  // chain because the guards above rule out a missing workspace at runtime
  // without narrowing the type — the workflow found in it is the proof.
  const labelRegistry = workspace?.labels ?? [];
  // Resolved against the vocabulary before anything reads it, so the filter, the
  // subtitle and the pills can never disagree about which labels are actually in
  // force. An address naming a deleted label is ignored rather than narrowing the
  // board to nothing (FR-025).
  const narrowing = resolveLabelNarrowing(labelNarrowing, labelRegistry);
  // The three separate props, composed into the one shape the shared filter row
  // and `filterBoard` both take. The props stay separate because the address
  // carries three parameters and the route writes each one on its own.
  const boardNarrowing: TaskNarrowing = {
    q: query,
    state: stateFilter,
    labels: narrowing,
    excludeSubtasks: !showSubtasks,
  };
  const isNarrowed =
    query !== undefined || stateFilter !== undefined || narrowing !== undefined;
  const activeTaskCount = tasks.filter((t) => t.status === 'active').length;
  const openCreate = () => {
    openDraft(workspaceId, workflow.id);
  };
  // A board with no tasks still renders in full: Backlog leftmost, Done
  // rightmost, every phase between them (FR-009). Standing a call to action in
  // front of the columns hid the one thing a first-time reader needs to see —
  // the shape of the workflow they are about to put work into. The call to
  // action (FR-015) moves into the start column, where that first task lands.
  const isEmptyBoard = tasks.length === 0;
  // Counted from the whole board, never from the filtered one: the summary has
  // to keep saying how many tasks are running while you are looking only at the
  // failed ones, or the way back out of a filter disappears.
  const activity =
    result?.ok === true
      ? // Label-narrowed but NOT state-narrowed. The three counts are the state
        // filter's own affordance, so they must keep reporting every state while
        // one of them is active — that is the way back out. They are not the
        // label filter's affordance, so continuing to count labels the reader has
        // filtered away would let a pill read "2 failed" when no failed task
        // carries the active label (FR-020). The text query narrows them for the
        // same reason the labels do: it is not the state pills' affordance.
        summariseActivity(
          filterBoard(result.board, { q: query, labels: narrowing }),
        )
      : { running: 0, waiting: 0, failed: 0 };
  const visibleBoard =
    result?.ok === true ? filterBoard(result.board, boardNarrowing) : null;

  return (
    // No `min-h-0`: the page scrolls, not the board (2026-09-29). A flex child
    // keeps its content's minimum height, so a tall board makes this section
    // taller than `<main>` and `<main>` — `overflow-auto` — scrolls the whole
    // page. `flex-1` still stretches a short board to the bottom of the window.
    <WorkspaceConcurrencyProvider limit={workspace?.concurrency?.limit}>
      <section className="relative flex flex-1 flex-col gap-6 max-md:px-4 max-md:pt-4 p-6">
        {/* The board names the workflow it is showing; switching between
          workflows is the Workflows section's job, so there is no picker here
          however many exist (spec 094). */}
        <PageHeader
          surface="inverse"
          eyebrow="Board"
          title={workflow.name}
          // The workflow row this board was opened from carries into this title,
          // as it already did for the builder. Keyed on the workflow's id, which
          // is the entity's own identifier and cannot be re-pointed (FR-028).
          titleViewTransitionName={`workflow-${workflow.id}`}
          // The same pill, in the same place, as Overview and Tasks (spec 122
          // FR-016): `live` is the polling condition itself, never a second
          // opinion.
          aside={
            <LiveStatus
              live={live}
              updatedAt={refresh.dataUpdatedAt}
              failed={refresh.isRefetchError}
              retrying={refresh.isFetching}
              onRetry={() => {
                void refresh.refetch();
              }}
            />
          }
          description={narrowingSummary(
            isNarrowed,
            visibleBoard ? countTasks(visibleBoard) : 0,
            activeTaskCount,
            'active task',
          )}
          actions={
            isPhone ? (
              <BoardPhoneActions
                workspaceId={workspaceId}
                workflowKey={workflowKey}
                canWork={canWork}
                showDiagram={showDiagram}
                onShowDiagramChange={onShowDiagramChange}
                onCreate={openCreate}
              />
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                {/* How busy the workspace is, always (spec 118): it replaced the
                queue notice, which spoke only once the workspace was full. */}
                <CapacityMeter
                  capacity={workspace?.concurrency}
                  workspaceId={workspaceId}
                  queue={queue}
                  isAdmin={isOrgAdmin}
                />
                {/*
              Two controls, deliberately not one three-state control (spec 094
              FR-014). Showing the diagram and editing the workflow are different
              intents, and folding them together would let an accidental click
              put somebody in an editor over live work.
            */}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  aria-pressed={showDiagram}
                  onClick={() => {
                    onShowDiagramChange?.(!showDiagram);
                  }}
                >
                  <NetworkIcon aria-hidden="true" />
                  {showDiagram ? 'Hide diagram' : 'Show diagram'}
                </Button>
                {/*
              A link, not a mode. The builder owns the draft state, the undo/redo
              stack and the unsaved-changes guard; hosting an editing mode here
              would mean duplicating all three, and the guard is the part that
              would fail quietly (FR-013, FR-019).
            */}
                <Link
                  to="/workspaces/$workspaceId/workflows/$workflowKey/builder"
                  params={{ workspaceId, workflowKey }}
                  // Both pages title themselves with the same workflow, so the
                  // title travels between them rather than the page blinking.
                  viewTransition={!reducedMotion}
                  className={buttonVariants({ variant: 'outline', size: 'sm' })}
                >
                  {/* Someone only reading the workspace opens the builder's
                    read-only view (spec 189), so the door says so. */}
                  {canWork ? (
                    <>
                      <PencilIcon aria-hidden="true" />
                      Edit workflow
                    </>
                  ) : (
                    <>
                      <EyeIcon aria-hidden="true" />
                      View workflow
                    </>
                  )}
                </Link>
                {canWork ? (
                  <Button type="button" size="sm" onClick={openCreate}>
                    <PlusIcon aria-hidden="true" />
                    New task
                  </Button>
                ) : null}
              </div>
            )
          }
        />

        {/*
        The diagram, beside the board rather than instead of it (FR-010), and
        mounted only when it is showing — which is what keeps React Flow out of
        every board reader's download (FR-011, constitution §6).
      */}
        {showDiagram ? (
          <div className="border-border bg-card flex h-[22rem] shrink-0 flex-col overflow-hidden rounded-lg border">
            <BoardDiagramLazy
              workspaceId={workspaceId}
              workflowKey={workflowKey}
            />
            {/*
            The way out of a glance and into the real thing.

            What sits above is deliberately read-only: no focusable nodes, no
            keyboard traversal, no step detail. Those live on the workflow's own
            diagram page, and without this link that page would be reachable only
            by typing its address — which is how a keyboard-accessible surface
            quietly stops existing.
          */}
            <div className="border-border flex shrink-0 justify-end border-t px-3 py-1.5">
              <Link
                to="/workspaces/$workspaceId/workflows/$workflowKey"
                params={{ workspaceId, workflowKey }}
                className="text-muted-foreground hover:text-foreground focus-visible:ring-ring rounded-sm text-xs underline underline-offset-2 focus-visible:ring-3 focus-visible:outline-none"
              >
                Open the full diagram
              </Link>
            </div>
          </div>
        ) : null}

        {/*
        What this board is *for*, when the workflow says so.

        Its own line rather than the header's `description` slot, which is
        already spoken for by `boardSubtitle` — that reports what the board is
        currently narrowed to, and is status. This is prose about the workflow
        and does not change as filters do; putting them in one slot would mean
        the description vanished the moment anyone filtered.

        Rendered only when there is something to say. An empty line reserved on
        every board would be a permanent cost for an optional field.
      */}
        {workflow.description ? (
          /*
          `text-sm` over the board's columns, capped at `max-w-6xl`. The prose
          measure (65ch) stopped it around 700px on a 1280px page — half the
          width empty, and the clamp eating text that had room to sit on the
          same line. The cap is kept rather than removed so the lines do not run
          the full width of an ultra-wide display.
        */
          <ExpandableText
            text={workflow.description}
            id="board-workflow-description"
            className="-mt-3 max-w-6xl text-sm"
          />
        ) : null}

        {/*
        Its own line, full width, and directly above the board it filters.

        It was in `PageHeader`'s `aside` — level with the title — which worked
        while the row was three small pills, and stopped working the moment it
        gained a search field: a 384px input beside a title and a New task
        button overflows the header, so the row wrapped and the state pills fell
        onto a second line underneath the search, out of line with everything.

        The placement is still the same on all three surfaces, which is what the
        consistency was actually about; it is the *slot* that moved, not one
        page's arrangement.

        It sits *below* the workflow description rather than above it. A filter
        acts on the board, so it belongs against the board; with the description
        between them, the control you press and the thing that changes were
        separated by a paragraph of unrelated prose. The description answers
        "what am I looking at", which is the title's question, so it stays with
        the title block.
      */}
        <TaskFilterRow
          registry={labelRegistry}
          narrowing={boardNarrowing}
          onChange={(next) => {
            if (next.q !== boardNarrowing.q) onQueryChange?.(next.q);
            if (next.state !== boardNarrowing.state) {
              onStateFilterChange?.(next.state);
            }
            if (next.labels !== boardNarrowing.labels) {
              onLabelNarrowingChange?.(next.labels);
            }
            if (next.excludeSubtasks !== boardNarrowing.excludeSubtasks) {
              setShowSubtasks(!next.excludeSubtasks);
            }
          }}
          stateCounts={activity}
          controls={{
            search: true,
            labels: true,
            state: true,
            hidden: true,
            subtasks: true,
          }}
          includeHidden={showHidden}
          onIncludeHiddenChange={setShowHidden}
        />

        {/*
        A board that will not start runs says so here, above the board and below
        the filter row (spec 101 US1). Above the canvas because it is a fact
        about everything in it, and below the filter row because it is not a
        control and must not sit among them.

        Absent unless a limit is actually reached, so an ordinary board is
        exactly as it was.
      */}
        <BoardLimitNotice
          workspaceId={workspaceId}
          workspaceName={workspace?.name ?? ''}
          workflowName={workflow.name}
          limits={limits}
        />

        {/* Absent unless this workspace requires each person's own account and
          the reader has none usable for one of its hosts (spec 160 US5). */}
        <PersonalAccountNotice workspaceId={workspaceId} />

        {/* Absent unless this workflow has lost an agent or a skill, which the
          run itself would never report (spec 107 US2). */}
        <MissingPartsNotice
          workspaceId={workspaceId}
          workflowKey={workflowKey}
        />

        {result && !result.ok ? (
          <BoardUnavailable reason={result.reason} />
        ) : visibleBoard ? (
          <>
            <BoardCanvasLazy
              workspaceId={workspaceId}
              workflowKey={workflowKey}
              workflowId={workflow.id}
              board={visibleBoard}
              createStepId={startStepId}
              onCreateTask={openCreate}
              onOpenTask={onOpenTask}
              // A filtered board is not an empty one; the first-task invitation
              // belongs only to a board that genuinely holds nothing.
              showFirstTaskPrompt={isEmptyBoard && !stateFilter && !narrowing}
              arrivedTaskIds={arrivals.arrived}
              onMoveReady={registerMove}
            />
            {/* After the canvas in the DOM: it floats over the canvas's lower edge
              (see `BoardSelectionBar`) and its buttons follow the cards in tab
              order, which is the order a selection is made in. */}
            <BoardSelectionBar
              workspaceId={workspaceId}
              steps={visibleBoard.columns.flatMap((column) => column.steps)}
              tasks={tasks}
              labelRegistry={labelRegistry}
            />
          </>
        ) : null}

        {openTaskId ? (
          <BoardStepsContext.Provider value={boardSteps}>
            <BoardMoveContext.Provider value={boardMove}>
              <TaskDrawer
                /*
            Keyed by the task, so opening a different one builds a new drawer
            rather than re-using this one. The drawer holds an unsaved
            description draft, and `openTaskId` comes from the address — moving
            between two `?task=` addresses re-renders without remounting, which
            would otherwise carry one task's draft into another task's field and
            save it there.
          */
                key={openTaskId}
                taskId={openTaskId}
                workspaceId={workspaceId}
                workflowKey={workflowKey}
                task={tasks.find((candidate) => candidate.id === openTaskId)}
                // Located against the unfiltered board, not `filtered`: narrowing the
                // board to one state must not make an open task look unplaced.
                location={
                  result?.ok === true
                    ? locateTask(result.board, openTaskId)
                    : undefined
                }
                // Every step on the board by id, so a declared transition can be
                // named by the step it runs rather than by the id it points at.
                // Built from the unfiltered board for the same reason the location
                // is: a narrowed board must not rename anything.
                stepLabels={stepLabels}
                steps={boardSteps}
                labelRegistry={labelRegistry}
                onNarrowToLabel={(labelId) => {
                  /*
              "Show me everything like this" (D7). The label joins whatever the
              board is already narrowed to rather than replacing it, so pressing
              two labels on two tasks builds the AND the rest of the feature
              means by a narrowing.

              The drawer closes, because the gesture is about the board and not
              about this task — leaving one task open over the answer would be
              asking for the result and then hiding it. Pressing a label the
              board already carries is therefore just "close and show me", not a
              no-op that appears broken.
            */
                  const current =
                    narrowing?.kind === 'labels' ? narrowing.ids : [];
                  const next = current.includes(labelId)
                    ? current
                    : [...current, labelId];
                  onLabelNarrowingChange?.({ kind: 'labels', ids: next });
                  onCloseTask?.();
                }}
                tab={openTab}
                onTabChange={(tab, options) => onTabChange?.(tab, options)}
                onClose={() => onCloseTask?.()}
                previewPath={previewPath}
                onPreviewClose={onPreviewClose}
              />
            </BoardMoveContext.Provider>
          </BoardStepsContext.Provider>
        ) : null}
      </section>
    </WorkspaceConcurrencyProvider>
  );
}
